import React, { useState, useEffect, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Paper,
  CircularProgress,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  LinearProgress,
  Collapse,
  Button,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Today as TodayIcon,
  RequestQuote,
  HistoryEdu as SignaturesIcon,
  People,
  Work,
  AdminPanelSettings,
  CalendarToday,
  Assessment,
  Settings,
  Logout,
  Notifications,
  Landscape,
  AccountCircle,
  Business as BusinessIcon,
  SupportAgent as SupportIcon,
  WbSunny,
  Thermostat,
  Air,
  AcUnit,
  LocalShipping,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Schedule,
  CheckCircle,
  Warning,
  Analytics,
  Assignment as TaskIcon,
  AssignmentTurnedIn as AssignmentIcon,
  ArrowBack,
  Grass as GrassIcon,
  Description as DocumentIcon,
  FactCheck as InspectionIcon,
  ExpandLess,
  ExpandMore,
  Construction as ConstructionIcon,
  PhoneAndroid as PhoneIcon,
  Email as EmailIcon,
  AccountBalanceWallet as BudgetIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { makeApiCall } from '../config/api';
import { formatCurrencyAbbreviated } from '../utils/currencyUtils';
import { canAccessPage, hasMinRole } from '../utils/roles';
import CRMDashboard from './CRM/CRMDashboard';
import CustomerDetailView from './CRM/CustomerDetailView';
import TicketList from './CRM/TicketList';
import TicketDetailPage from './CRM/TicketDetailPage';
import TaskList from './CRM/TaskList';
import UserManagement from './Admin/UserManagement';
import AdminEmployeeManagement from './Admin/EmployeeManagement';
import TurfAdminPanel from './Admin/TurfAdminPanel';
import ServicePricingAdmin from './Admin/ServicePricingAdmin';
import QuotePdfList from './Admin/QuotePdfList';
import SignaturesPage from './Esign/SignaturesPage';
import FleetManagement from './Admin/FleetManagement';
import TimesheetManagement from './Admin/TimesheetManagement';
import UserProfile from './UserProfile';
import ReportsMain from './Reports/ReportsMain';
import SchedulerManagement from './Admin/SchedulerManagement';
import OrphanedJobSites from './Admin/OrphanedJobSites';
import SystemEmailSettings from './Admin/SystemEmailSettings';
import BudgetAdmin from './Admin/Budget/BudgetAdmin';
import HRDashboard from './HR/HRDashboard';
import FormIndex from './HR/Forms/FormIndex';
import DirectDepositForm from './HR/Forms/forms/DirectDepositForm';
import CellPhoneForm from './HR/Forms/forms/CellPhoneForm';
import DrugAlcoholPolicyForm from './HR/Forms/forms/DrugAlcoholPolicyForm';
import CompanyPolicyForm from './HR/Forms/forms/CompanyPolicyForm';
import PayrollAgreementForm from './HR/Forms/forms/PayrollAgreementForm';
import UniformForm from './HR/Forms/forms/UniformForm';
import EmployeeEmergencyInfoForm from './HR/Forms/forms/EmployeeEmergencyInfoForm';
import GasAirMilesCardForm from './HR/Forms/forms/GasAirMilesCardForm';
import {
  InterestConflictPolicyForm,
  HumanResourcesPolicyForm,
  DisciplinePolicyForm,
  ViolencePreventionPolicyForm,
  HealthSafetyPolicyForm,
  EquipmentPolicyForm,
  EmploymentStandardsActForm,
  ElectronicDevicePolicyForm
} from './HR/Forms/forms/AllSimpleForms';
import TimesheetCalendar from './HR/TimesheetCalendar';
import LeaveManagement from './HR/LeaveManagement';
import AbsenceTracking from './HR/AbsenceTracking';
import EmployeeDiscipline from './HR/EmployeeDiscipline';
import PerformanceReviews from './HR/PerformanceReviews';
import HRDocuments from './HR/HRDocuments';
import EmployeeFormsAdmin from './HR/EmployeeFormsAdmin';
import UnsignEmployeeForms from './Admin/UnsignEmployeeForms';
import QuoteList from './Quotes/QuoteList';
import PendingQuotesPage from './Quotes/PendingQuotesPage';
import JobManagement from './Jobs/JobManagement';
import JobDetailView from './Jobs/JobDetailView';
import CalendarView from './Jobs/CalendarView';
import WeatherWidget from './WeatherWidget';
import TimeDisplay from './TimeDisplay';
import InsightsCenter from './Insights/InsightsCenter';
import NotificationDropdown from './NotificationDropdown';
import LanguageSwitcher from './LanguageSwitcher';
import TimeTrackingWidget from './TimeTracking/TimeTrackingWidget';
import TodaysJobsWidget from './Jobs/TodaysJobsWidget';
import DocumentsPage from './DocumentsPage';
import RouteSetup from './RoutePlanning/RouteSetup';
import RoutesList from './RoutePlanning/RoutesList';
import OperationsDashboard from './Dashboard/OperationsDashboard';
import SalesTracker from './Sales/SalesTracker';
import VehicleInspections from './Inspections/VehicleInspections';
import InspectionWizard from './Inspections/InspectionWizard';
import InspectionDetailView from './Inspections/InspectionDetailView';
import InspectionsDashboardWidget from './Inspections/InspectionsDashboardWidget';
import ErrorBoundary from './ErrorBoundary';

const drawerWidth = 280;

const DASHBOARD_PAGE_PATHS = {
  operations: '/dashboard/operations',
  dashboard: '/dashboard',
  quotes: '/dashboard/quotes',
  'pending-quotes': '/dashboard/quotes/pending',
  signatures: '/dashboard/signatures',
  customers: '/dashboard/customers',
  'sales-tracker': '/dashboard/sales-tracker',
  tickets: '/dashboard/tickets',
  tasks: '/dashboard/tasks',
  jobs: '/dashboard/jobs',
  // Without this, "Full Live Tracking" on Operations fell back to /dashboard.
  'live-tracking': '/dashboard/live-tracking',
  'summer-logs': '/dashboard/summer-logs',
  'winter-logs': '/dashboard/winter-logs',
  'construction-logs': '/dashboard/construction-logs',
  'route-setup': '/dashboard/route-setup',
  'routes-list': '/dashboard/routes-list',
  inspections: '/dashboard/inspections',
  'inspection-new': '/dashboard/inspections/new',
  documents: '/dashboard/documents',
  'employee-forms': '/dashboard/employee-forms',
  calendar: '/dashboard/calendar',
  reports: '/dashboard/reports',
  profile: '/dashboard/profile',
  users: '/dashboard/admin/users',
  employees: '/dashboard/admin/employees',
  'service-pricing': '/dashboard/admin/service-pricing',
  budget: '/dashboard/admin/budget',
  'turf-admin': '/dashboard/admin/turf',
  'quote-pdf-list': '/dashboard/admin/quote-pdfs',
  'quote-report': '/dashboard/admin/quote-report',
  'fleet-management': '/dashboard/admin/fleet',
  timesheets: '/dashboard/admin/timesheets',
  scheduler: '/dashboard/admin/scheduler',
  'orphaned-job-sites': '/dashboard/admin/orphaned-job-sites',
  'system-email-settings': '/dashboard/admin/system-email',
  'unsign-forms': '/dashboard/admin/unsign-forms',
  'hr-dashboard': '/dashboard/hr',
  'timesheet-calendar': '/dashboard/hr/timesheet-calendar',
  'leave-management': '/dashboard/hr/leave-management',
  'absence-tracking': '/dashboard/hr/absence-tracking',
  'employee-discipline': '/dashboard/hr/employee-discipline',
  'performance-reviews': '/dashboard/hr/performance-reviews',
  'hr-documents': '/dashboard/hr/documents',
  'employee-forms-admin': '/dashboard/hr/employee-forms'
};

const ADMIN_ROUTE_PAGES = {
  users: 'users',
  employees: 'employees',
  'service-pricing': 'service-pricing',
  budget: 'budget',
  turf: 'turf-admin',
  'quote-pdfs': 'quote-pdf-list',
  'quote-report': 'quote-report',
  fleet: 'fleet-management',
  timesheets: 'timesheets',
  scheduler: 'scheduler',
  'orphaned-job-sites': 'orphaned-job-sites',
  'system-email': 'system-email-settings',
  'unsign-forms': 'unsign-forms'
};

const HR_ROUTE_PAGES = {
  '': 'hr-dashboard',
  'timesheet-calendar': 'timesheet-calendar',
  'leave-management': 'leave-management',
  'absence-tracking': 'absence-tracking',
  'employee-discipline': 'employee-discipline',
  'performance-reviews': 'performance-reviews',
  documents: 'hr-documents',
  'employee-forms': 'employee-forms-admin'
};

const createEmptyRouteIds = () => ({
  customerDetailId: null,
  ticketDetailId: null,
  taskDetailId: null,
  jobDetailId: null,
  inspectionDetailId: null
});

const normalizeDashboardPath = (pathname) => {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || '/dashboard';
};

const getDashboardRouteState = (pathname, search) => {
  const path = normalizeDashboardPath(pathname);
  const params = new URLSearchParams(search);
  const baseState = {
    selectedPage: 'dashboard',
    ...createEmptyRouteIds(),
    jobsInitialFilter: null,
    quotesInitialStatus: params.get('status') || '',
    reportsInitialReportId: params.get('report') || null,
    quoteActionKey: params.get('quoteId') || ''
  };

  if (path === '/dashboard') {
    return baseState;
  }

  if (!path.startsWith('/dashboard/')) {
    return null;
  }

  const parts = path.slice('/dashboard/'.length).split('/');
  const [section, id] = parts;

  if (section === 'customers') {
    return id
      ? { ...baseState, selectedPage: 'customer-detail', customerDetailId: id }
      : { ...baseState, selectedPage: 'customers' };
  }

  if (section === 'tickets') {
    return id
      ? { ...baseState, selectedPage: 'ticket-detail', ticketDetailId: id }
      : { ...baseState, selectedPage: 'tickets' };
  }

  if (section === 'tasks') {
    return { ...baseState, selectedPage: 'tasks', taskDetailId: id || null };
  }

  if (section === 'jobs') {
    const jobsInitialFilter = {
      ...(params.get('status') ? { status: params.get('status') } : {}),
      ...(params.get('job_type') ? { jobType: params.get('job_type') } : {})
    };
    return id
      ? { ...baseState, selectedPage: 'job-detail', jobDetailId: id }
      : {
          ...baseState,
          selectedPage: 'jobs',
          jobsInitialFilter: Object.keys(jobsInitialFilter).length ? jobsInitialFilter : null
        };
  }

  if (section === 'quotes') {
    return id === 'pending'
      ? { ...baseState, selectedPage: 'pending-quotes', quotesInitialStatus: 'pending' }
      : { ...baseState, selectedPage: 'quotes' };
  }

  if (section === 'inspections') {
    if (id === 'new') return { ...baseState, selectedPage: 'inspection-new' };
    return id
      ? { ...baseState, selectedPage: 'inspection-detail', inspectionDetailId: id }
      : { ...baseState, selectedPage: 'inspections' };
  }

  if (section === 'admin') {
    const page = ADMIN_ROUTE_PAGES[id];
    return page ? { ...baseState, selectedPage: page } : null;
  }

  if (section === 'hr') {
    const page = HR_ROUTE_PAGES[id || ''];
    return page ? { ...baseState, selectedPage: page } : null;
  }

  if (DASHBOARD_PAGE_PATHS[section]) {
    return { ...baseState, selectedPage: section };
  }

  return null;
};

// Static data that doesn't change

const serviceDistributionData = [
  { name: 'services.landscaping', value: 35, color: '#2e7d32' },
  { name: 'services.snowRemoval', value: 25, color: '#1976d2' },
  { name: 'services.maintenance', value: 20, color: '#f57c00' },
  { name: 'services.installation', value: 15, color: '#d32f2f' },
  { name: 'services.other', value: 5, color: '#7b1fa2' },
];

const weeklyPerformanceData = [
  { day: 'days.mon', completed: 12, scheduled: 15, efficiency: 80 },
  { day: 'days.tue', completed: 18, scheduled: 20, efficiency: 90 },
  { day: 'days.wed', completed: 14, scheduled: 16, efficiency: 87 },
  { day: 'days.thu', completed: 22, scheduled: 24, efficiency: 92 },
  { day: 'days.fri', completed: 19, scheduled: 21, efficiency: 90 },
  { day: 'days.sat', completed: 16, scheduled: 18, efficiency: 89 },
  { day: 'days.sun', completed: 8, scheduled: 10, efficiency: 80 },
];

const Dashboard = () => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPage, setSelectedPage] = useState('dashboard');
  const [customerDetailId, setCustomerDetailId] = useState(null);
  const [ticketDetailId, setTicketDetailId] = useState(null);
  const [taskDetailId, setTaskDetailId] = useState(null);
  const [jobDetailId, setJobDetailId] = useState(null);
  const [inspectionDetailId, setInspectionDetailId] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [formLanguage, setFormLanguage] = useState('en');
  const [jobsInitialFilter, setJobsInitialFilter] = useState(null);
  const [quotesInitialStatus, setQuotesInitialStatus] = useState('');
  const [reportsInitialReportId, setReportsInitialReportId] = useState(null);
  const [quoteActionKey, setQuoteActionKey] = useState('');
  const [pendingApprovalOpen, setPendingApprovalOpen] = useState(false);
  const [pendingApprovalQuotes, setPendingApprovalQuotes] = useState([]);
  const [expandedMainMenus, setExpandedMainMenus] = useState({});
  const [expandedAdminMenus, setExpandedAdminMenus] = useState({});
  const [showProfile, setShowProfile] = useState(false);
  const [liveMetrics, setLiveMetrics] = useState({
    activeJobs: 0,
    pendingQuotes: 0,
    newCustomers: 4,
    revenue: 156750,
    profit: 47250,
    efficiency: 89,
    customerSatisfaction: 4.6
  });
  const [chartsInitiallyLoaded, setChartsInitiallyLoaded] = useState(false);

  const navigateToPage = (page, options = {}) => {
    const path = DASHBOARD_PAGE_PATHS[page] || '/dashboard';
    routerNavigate(`${path}${options.search || ''}`, { replace: options.replace || false });
  };

  const navigateToDashboardUrl = (url, options = {}) => {
    routerNavigate(url, { replace: options.replace || false, state: options.state });
  };

  // Keep the dashboard shell in sync with the browser URL so direct links,
  // refreshes, and browser back/forward restore the same screen.
  useEffect(() => {
    const navState = location.state;
    if (navState?.navigateTo) {
      if (navState.navigateTo === 'quotes') {
        const params = new URLSearchParams(location.search);
        if (navState.entityId) params.set('quoteId', navState.entityId);
        if (navState.quoteAction) params.set('action', navState.quoteAction);
        routerNavigate(`/dashboard/quotes?${params.toString()}`, { replace: true, state: {} });
        return;
      } else if (navState.navigateTo === 'job-detail' && navState.entityId) {
        routerNavigate(`/dashboard/jobs/${navState.entityId}`, { replace: true, state: {} });
        return;
      }
    }

    if (normalizeDashboardPath(location.pathname) === '/dashboard/hr/employees') {
      routerNavigate('/dashboard/admin/employees', { replace: true });
      return;
    }

    const routeState = getDashboardRouteState(location.pathname, location.search);
    if (!routeState) {
      routerNavigate('/dashboard', { replace: true });
      return;
    }

    const params = new URLSearchParams(location.search);
    const quoteId = params.get('quoteId');
    if (routeState.selectedPage === 'quotes' && quoteId) {
      sessionStorage.setItem('selectedQuoteId', quoteId);
      const quoteAction = params.get('action');
      if (quoteAction) {
        sessionStorage.setItem('selectedQuoteAction', quoteAction);
      }
    }

    setSelectedPage(routeState.selectedPage);
    setCustomerDetailId(routeState.customerDetailId);
    setTicketDetailId(routeState.ticketDetailId);
    setTaskDetailId(routeState.taskDetailId);
    setJobDetailId(routeState.jobDetailId);
    setInspectionDetailId(routeState.inspectionDetailId);
    setJobsInitialFilter(routeState.jobsInitialFilter);
    setQuotesInitialStatus(routeState.quotesInitialStatus);
    setReportsInitialReportId(routeState.reportsInitialReportId);
    setQuoteActionKey(routeState.quoteActionKey);
  }, [location.pathname, location.search, location.state, routerNavigate]);
  
  // Chart data in state for smooth updates
  const [revenueData, setRevenueData] = useState([
    { month: 'Jan', revenue: 45000, profit: 12000, costs: 33000 },
    { month: 'Feb', revenue: 52000, profit: 15000, costs: 37000 },
    { month: 'Mar', revenue: 48000, profit: 13500, costs: 34500 },
    { month: 'Apr', revenue: 61000, profit: 18000, costs: 43000 },
    { month: 'May', revenue: 73000, profit: 22000, costs: 51000 },
    { month: 'Jun', revenue: 68000, profit: 19500, costs: 48500 },
  ]);

  const [truckData, setTruckData] = useState([
    { truck: 'Truck-01', profit: 8500, revenue: 12000, utilization: 92 },
    { truck: 'Truck-02', profit: 7200, revenue: 10500, utilization: 88 },
    { truck: 'Truck-03', profit: 6800, revenue: 9800, utilization: 85 },
    { truck: 'Truck-04', profit: 9100, revenue: 13200, utilization: 95 },
    { truck: 'Truck-05', profit: 5900, revenue: 8900, utilization: 78 },
  ]);

  // --- Simulated KPI datasets (legacy-inspired) ---
  const [laborEfficiencyData] = useState([
    { week: 'Wk1', efficiency: 82 },
    { week: 'Wk2', efficiency: 85 },
    { week: 'Wk3', efficiency: 88 },
    { week: 'Wk4', efficiency: 86 },
  ]);

  const [jobProfitData] = useState([
    { job: 'Job-101', margin: 18 },
    { job: 'Job-102', margin: 22 },
    { job: 'Job-103', margin: 15 },
    { job: 'Job-104', margin: 26 },
  ]);

  const quoteFunnelData = [
    { name: 'quotes.draft', value: 15, color: '#607d8b' },
    { name: 'quotes.sent',  value: 22, color: '#1976d2' },
    { name: 'quotes.won',   value: 12, color: '#2e7d32' },
    { name: 'quotes.lost',  value: 7,  color: '#d32f2f' },
  ];

  const [ticketSlaData] = useState([
    { category: '<24h', count: 12 },
    { category: '24-48h', count: 20 },
    { category: '48-72h', count: 9 },
    { category: '>72h', count: 6 },
  ]);

  const [fleetUtilData] = useState([
    { day: 'days.mon', utilization: 85 },
    { day: 'days.tue', utilization: 88 },
    { day: 'days.wed', utilization: 82 },
    { day: 'days.thu', utilization: 90 },
    { day: 'days.fri', utilization: 87 },
    { day: 'days.sat', utilization: 79 },
    { day: 'days.sun', utilization: 65 },
  ]);

  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const showBackToCrew = Boolean(user && (user.is_field_crew || user.user_type === 'crew_manager'));

  useEffect(() => {
    if (!user?.id || !['admin', 'superadmin'].includes(user.user_type)) return;
    // Same pause as QUOTE_APPROVAL_PAUSED_USERNAMES in quotes.js.
    if (['doug'].includes(String(user.username || '').toLowerCase())) return;
    const seenKey = `pending-quote-login-popup:${user.id}`;
    if (sessionStorage.getItem(seenKey) === 'true') return;

    let cancelled = false;
    const loadPendingApprovals = async () => {
      try {
        const response = await makeApiCall('api/quotes?page=1&limit=10&status=pending');
        const data = await response.json().catch(() => ({}));
        if (!response.ok || cancelled) return;
        const pending = data.quotes || [];
        setPendingApprovalQuotes(pending);
        if (pending.length > 0) setPendingApprovalOpen(true);
        sessionStorage.setItem(seenKey, 'true');
      } catch (loadError) {
        console.error('Failed to load pending quote approvals:', loadError);
      }
    };
    loadPendingApprovals();
    return () => { cancelled = true; };
  }, [user?.id, user?.user_type]);

  useEffect(() => {
    let isMounted = true;

    const loadDashboardMetricCounts = async () => {
      try {
        // TKT-182: scope Active Jobs to TODAY so the dashboard tile
        // shows the number of jobs the office actually has on the
        // schedule right now. The unscoped /jobs?status=active query
        // counts every historical active_job and was returning
        // five-figure numbers nobody could interpret.
        const todayIso = new Date().toISOString().slice(0, 10);
        const activeJobsUrl =
          `api/jobs?page=1&limit=1&status=active&date_from=${todayIso}&date_to=${todayIso}`;
        const [activeJobsRes, pendingQuotesRes] = await Promise.all([
          makeApiCall(activeJobsUrl),
          makeApiCall('api/quotes?page=1&limit=1&status=pending')
        ]);

        const [activeJobsData, pendingQuotesData] = await Promise.all([
          activeJobsRes.ok ? activeJobsRes.json() : Promise.resolve(null),
          pendingQuotesRes.ok ? pendingQuotesRes.json() : Promise.resolve(null)
        ]);

        if (!isMounted) return;

        setLiveMetrics((prev) => ({
          ...prev,
          activeJobs: activeJobsData?.pagination?.total ?? prev.activeJobs,
          pendingQuotes: pendingQuotesData?.pagination?.total ?? prev.pendingQuotes
        }));
      } catch (error) {
        console.error('Failed to load dashboard metric counts:', error);
      }
    };

    loadDashboardMetricCounts();

    return () => {
      isMounted = false;
    };
  }, []);

  // Disable intro animations after initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setChartsInitiallyLoaded(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // ALL LIVE UPDATES DISABLED - Static dashboard
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setLiveMetrics(prev => ({
  //       ...prev,
  //       activeJobs: Math.max(5, prev.activeJobs + Math.floor(Math.random() * 3) - 1),
  //       pendingQuotes: Math.max(8, prev.pendingQuotes + Math.floor(Math.random() * 3) - 1),
  //       efficiency: Math.max(75, Math.min(98, prev.efficiency + Math.floor(Math.random() * 5) - 2)),
  //       revenue: prev.revenue + Math.floor(Math.random() * 500) - 250,
  //       customerSatisfaction: Math.max(3.5, Math.min(5.0, prev.customerSatisfaction + (Math.random() * 0.2 - 0.1))),
  //       newCustomers: Math.max(1, prev.newCustomers + Math.floor(Math.random() * 3) - 1),
  //     }));
  //   }, 5000);
  //   return () => clearInterval(interval);
  // }, []);

  const clearDashboardDrilldownState = () => {
    setJobsInitialFilter(null);
    setQuotesInitialStatus('');
    setReportsInitialReportId(null);
  };

  const handleMetricCardNavigate = ({ page, jobsFilter = null, quoteStatus = '', reportId = null }) => {
    clearDashboardDrilldownState();
    const params = new URLSearchParams();
    if (jobsFilter?.status) params.set('status', jobsFilter.status);
    if (jobsFilter?.jobType) params.set('job_type', jobsFilter.jobType);
    if (quoteStatus) params.set('status', quoteStatus);
    if (reportId) params.set('report', reportId);
    navigateToPage(page, { search: params.toString() ? `?${params.toString()}` : '' });
    setMobileOpen(false);
  };

  const DemoWatermark = ({ compact = false }) => (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: compact ? 12 : '50%',
        right: compact ? 12 : '50%',
        transform: compact ? 'none' : 'translate(50%, -50%) rotate(-18deg)',
        px: compact ? 1 : 2,
        py: compact ? 0.25 : 0.5,
        border: '2px solid rgba(117, 117, 117, 0.5)',
        borderRadius: 1,
        color: 'rgba(97, 97, 97, 0.65)',
        fontWeight: 900,
        fontSize: compact ? '0.7rem' : { xs: '1.6rem', md: '2.5rem' },
        letterSpacing: compact ? '0.08em' : '0.18em',
        textTransform: 'uppercase',
        pointerEvents: 'none',
        zIndex: 2,
        bgcolor: 'rgba(255, 255, 255, 0.55)'
      }}
    >
      {t('common.demoData')}
    </Box>
  );

  // TKT-182: hide every widget flagged as demo data. Paramount keeps
  // asking what is real vs placeholder; the cleanest fix until each
  // chart is wired to live data is to not render the demo ones at all.
  // Flip this to false to show the watermarked placeholders again.
  const HIDE_DEMO_WIDGETS = true;

  const StatCard = ({ title, value, subtitle, icon, color, trend, trendValue, onClick, ariaLabel, demo = false }) => {
    // TKT-182: hide demo-only KPI cards entirely.
    if (demo && HIDE_DEMO_WIDGETS) return null;
    const content = (
      <CardContent sx={{ 
        flexGrow: 1, 
        width: '100%',
        p: 3,
        '&:last-child': { pb: 3 }, // Ensure consistent padding
        filter: demo ? 'grayscale(0.8)' : undefined,
        opacity: demo ? 0.62 : 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 1, 
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {React.cloneElement(icon, { sx: { color: color, fontSize: '1.5rem' } })}
          </Box>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend === 'up' ? (
                <TrendingUp sx={{ color: '#2e7d32', fontSize: '1rem' }} />
              ) : (
                <TrendingDown sx={{ color: '#d32f2f', fontSize: '1rem' }} />
              )}
              <Typography variant="caption" sx={{ color: trend === 'up' ? '#2e7d32' : '#d32f2f', fontWeight: 'bold' }}>
                {trendValue}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: color, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </CardContent>
    );

    return (
      <Card sx={{ 
        height: '100%', 
        width: '100%',
        display: 'flex', 
        flexDirection: 'column',
        minWidth: 0, // Allows card to shrink below content size
        position: 'relative',
        overflow: 'hidden',
        bgcolor: demo ? '#f5f5f5' : undefined,
        border: demo ? '1px dashed rgba(117, 117, 117, 0.5)' : undefined,
        transition: onClick ? 'transform 0.2s, box-shadow 0.2s' : undefined,
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4
        } : undefined
      }}>
        {onClick ? (
          <CardActionArea
            onClick={onClick}
            aria-label={ariaLabel}
            sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
          >
            {content}
            {demo && <DemoWatermark compact />}
          </CardActionArea>
        ) : (
          <>
            {content}
            {demo && <DemoWatermark compact />}
          </>
        )}
      </Card>
    );
  };

  const ChartCard = memo(({ title, children, height = 300, demo = false }) => {
    if (demo && HIDE_DEMO_WIDGETS) return null;
    return (
      <Card sx={{ 
        height: '100%', 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: demo ? '#f5f5f5' : undefined,
        border: demo ? '1px dashed rgba(117, 117, 117, 0.5)' : undefined
      }}>
        <CardContent sx={{ 
          flexGrow: 1,
          width: '100%',
          p: 2,
          '&:last-child': { pb: 2 },
          filter: demo ? 'grayscale(0.8)' : undefined,
          opacity: demo ? 0.62 : 1
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#000062' }}>
            {title}
          </Typography>
          <Box sx={{ height: height, width: '100%' }}>
            {children}
          </Box>
        </CardContent>
        {demo && <DemoWatermark />}
      </Card>
    );
  });

  // Completely static chart components with no interactions
  const RevenueChart = memo(() => (
    <ChartCard title={t('charts.revenueAndProfitTrends')} height={400} demo>
      <Box sx={{ width: '100%', height: 400 }}>
        <LineChart 
          width={800}
          height={400}
          data={revenueData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip 
            formatter={(value) => [`$${value.toLocaleString()}`, '']}
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '12px'
            }}
            animationDuration={150}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="revenue" 
            stroke="#1976d2" 
            strokeWidth={3}
            name={t('charts.revenue')}
            isAnimationActive={!chartsInitiallyLoaded}
            animationDuration={1500}
            animationEasing="ease-in-out"
            dot={false}
            activeDot={{ r: 4, fill: "#1976d2" }}
          />
          <Line 
            type="monotone" 
            dataKey="profit" 
            stroke="#2e7d32" 
            strokeWidth={3}
            name={t('charts.profit')}
            isAnimationActive={!chartsInitiallyLoaded}
            animationDuration={1500}
            animationEasing="ease-in-out"
            dot={false}
            activeDot={{ r: 4, fill: "#2e7d32" }}
          />
        </LineChart>
      </Box>
    </ChartCard>
  ));

  const ServiceChart = memo(() => (
    <ChartCard title={t('charts.serviceDistribution')} height={400} demo>
      <Box sx={{ width: '100%', height: 400 }}>
        <PieChart width={300} height={400}>
          <Pie
            data={serviceDistributionData}
            cx={150}
            cy={200}
            labelLine={false}
            label={({ name, percent }) => `${t(name)} ${(percent * 100).toFixed(0)}%`}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
            isAnimationActive={!chartsInitiallyLoaded}
            animationDuration={1500}
            animationEasing="ease-in-out"
          >
            {serviceDistributionData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value, name, props) => [`${value}%`, props.payload.name]}
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '12px'
            }}
            animationDuration={150}
          />
        </PieChart>
      </Box>
    </ChartCard>
  ));

  const TruckChart = memo(() => (
    <ChartCard title={t('charts.truckProfitabilityAnalysis')} height={350} demo>
      <Box sx={{ width: '100%', height: 350 }}>
        <BarChart 
          width={700}
          height={350}
          data={truckData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="truck" />
          <YAxis />
          <Tooltip 
            formatter={(value) => [`$${value.toLocaleString()}`, '']}
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '12px'
            }}
            animationDuration={150}
          />
          <Legend />
          <Bar 
            dataKey="profit" 
            fill="#2e7d32" 
            name={t('charts.profit')} 
            isAnimationActive={!chartsInitiallyLoaded}
            animationDuration={1500}
            animationEasing="ease-in-out"
          />
          <Bar 
            dataKey="revenue" 
            fill="#1976d2" 
            name={t('charts.revenue')} 
            isAnimationActive={!chartsInitiallyLoaded}
            animationDuration={1500}
            animationEasing="ease-in-out"
          />
        </BarChart>
      </Box>
    </ChartCard>
  ));

  const WeeklyChart = memo(() => (
    <ChartCard title={t('charts.weeklyJobPerformance')} height={350} demo>
      <Box sx={{ width: '100%', height: 350 }}>
        <AreaChart 
          width={400}
          height={350}
          data={weeklyPerformanceData}
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={(d) => t(d.day)} />
          <YAxis />
          <Tooltip 
            formatter={(value) => [value, '']}
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '12px'
            }}
            animationDuration={150}
          />
          <Area 
            type="monotone" 
            dataKey="completed" 
            stackId="1" 
            stroke="#2e7d32" 
            fill="#2e7d32" 
            name={t('charts.completed')}
            isAnimationActive={!chartsInitiallyLoaded}
            animationDuration={1500}
            animationEasing="ease-in-out"
          />
          <Area 
            type="monotone" 
            dataKey="scheduled" 
            stackId="2" 
            stroke="#de6127" 
            fill="#de6127" 
            name={t('charts.scheduled')}
            isAnimationActive={!chartsInitiallyLoaded}
            animationDuration={1500}
            animationEasing="ease-in-out"
          />
        </AreaChart>
      </Box>
    </ChartCard>
  ));

  // === NEW KPI WIDGETS ===
  const LaborEfficiencyChart = memo(() => (
    <ChartCard title={t('charts.laborEfficiency')} height={300} demo>
      <LineChart width={600} height={300} data={laborEfficiencyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="week" />
        <YAxis />
        <Tooltip formatter={(v)=>[`${v}%`, t('charts.efficiency')]} contentStyle={{ backgroundColor:'rgba(255,255,255,0.95)', border:'1px solid #ccc', borderRadius:'4px', fontSize:'12px' }} animationDuration={150} />
        <Legend />
        <Line type="monotone" dataKey="efficiency" stroke="#9c27b0" strokeWidth={3} name={t('charts.efficiency')} isAnimationActive={!chartsInitiallyLoaded} animationDuration={1500} animationEasing="ease-in-out" dot={false} />
      </LineChart>
    </ChartCard>
  ));

  const JobProfitChart = memo(() => (
    <ChartCard title={t('charts.jobProfitMargins')} height={300} demo>
      <BarChart width={600} height={300} data={jobProfitData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="job" />
        <YAxis />
        <Tooltip formatter={(v)=>[`${v}%`, t('charts.margin')]} contentStyle={{ backgroundColor:'rgba(255,255,255,0.95)', border:'1px solid #ccc', borderRadius:'4px', fontSize:'12px' }} animationDuration={150} />
        <Legend />
        <Bar dataKey="margin" fill="#ff9800" name={t('charts.marginPercent')} isAnimationActive={!chartsInitiallyLoaded} animationDuration={1500} animationEasing="ease-in-out" />
      </BarChart>
    </ChartCard>
  ));

  const QuoteFunnelChart = memo(() => (
    <ChartCard title={t('charts.quoteFunnel')} height={300} demo>
      <PieChart width={300} height={300}>
        <Pie data={quoteFunnelData} cx={150} cy={150} outerRadius={100} labelLine={false} label={({ name, percent }) => `${t(name)} ${(percent * 100).toFixed(0)}%`} dataKey="value" isAnimationActive={!chartsInitiallyLoaded} animationDuration={1500} animationEasing="ease-in-out" >
          {quoteFunnelData.map((entry, idx) => <Cell key={`qf-${idx}`} fill={entry.color} />)}
        </Pie>
        <Tooltip contentStyle={{ backgroundColor:'rgba(255,255,255,0.95)', border:'1px solid #ccc', borderRadius:'4px', fontSize:'12px' }} animationDuration={150} />
      </PieChart>
    </ChartCard>
  ));

  const TicketSLAChart = memo(() => (
    <ChartCard title={t('charts.ticketSLACompliance')} height={300} demo>
      <BarChart width={600} height={300} data={ticketSlaData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="category" />
        <YAxis />
        <Tooltip formatter={(v)=>[v, t('charts.tickets')]} contentStyle={{ backgroundColor:'rgba(255,255,255,0.95)', border:'1px solid #ccc', borderRadius:'4px', fontSize:'12px' }} animationDuration={150} />
        <Legend />
        <Bar dataKey="count" fill="#03a9f4" name={t('charts.tickets')} isAnimationActive={!chartsInitiallyLoaded} animationDuration={1500} animationEasing="ease-in-out" />
      </BarChart>
    </ChartCard>
  ));

  const FleetUtilizationChart = memo(() => (
    <ChartCard title={t('charts.fleetUtilization')} height={300} demo>
      <AreaChart width={900} height={300} data={fleetUtilData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <defs>
          <linearGradient id="utilColor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="day" />
        <YAxis />
        <Tooltip formatter={(v)=>[`${v}%`, t('charts.utilization')]} contentStyle={{ backgroundColor:'rgba(255,255,255,0.95)', border:'1px solid #ccc', borderRadius:'4px', fontSize:'12px' }} animationDuration={150} />
        <Area type="monotone" dataKey="utilization" stroke="#1976d2" fillOpacity={1} fill="url(#utilColor)" name={t('charts.utilizationPercent')} isAnimationActive={!chartsInitiallyLoaded} animationDuration={1500} animationEasing="ease-in-out" />
      </AreaChart>
    </ChartCard>
  ));

  // Weather component for header - now using live data
  // You can customize location by passing props like: location="Chicago,IL" or lat/lon coordinates

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    setShowProfile(true);
    navigateToPage('profile');
    handleProfileMenuClose();
  };

  const handlePendingQuoteOpen = (quoteId) => {
    setPendingApprovalOpen(false);
    navigateToPage('quotes', {
      search: `?quoteId=${encodeURIComponent(quoteId)}&action=approval`
    });
  };

  const handleLogout = () => {
    if (user?.id) {
      sessionStorage.removeItem(`pending-quote-login-popup:${user.id}`);
    }
    logout();
    handleProfileMenuClose();
  };

  const handleAdminMenuToggle = (menuId) => {
    setExpandedAdminMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleMainMenuToggle = (menuId) => {
    setExpandedMainMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  // Handle navigation to customer detail
  const handleNavigateToCustomer = (customerId) => {
    navigateToDashboardUrl(`/dashboard/customers/${customerId}`);
  };

  // Handle back to customers list
  const handleBackToCustomers = () => {
    navigateToPage('customers');
  };

  // Handle navigation to ticket detail
  const handleNavigateToTicket = (ticketId) => {
    navigateToDashboardUrl(`/dashboard/tickets/${ticketId}`);
  };

  // Handle back to tickets list
  const handleBackToTickets = () => {
    navigateToPage('tickets');
  };

  const handleNavigateToTask = (taskId, mode = 'view') => {
    // Store the mode for the TaskList component to use
    sessionStorage.setItem('taskViewMode', mode);
    navigateToDashboardUrl(`/dashboard/tasks/${taskId}`);
  };

  const handleBackToTasks = () => {
    navigateToPage('tasks');
  };

  const handleNavigateToJob = (jobId) => {
    navigateToDashboardUrl(`/dashboard/jobs/${jobId}`);
  };

  const handleBackToJobs = () => {
    navigateToPage('jobs');
  };

  const handleNavigateToInspection = (inspectionId) => {
    navigateToDashboardUrl(`/dashboard/inspections/${inspectionId}`);
  };

  const handleBackToInspections = () => {
    navigateToPage('inspections');
  };

  const allMenuItems = [
    { id: 'operations', label: t('navigation.operations'), icon: <TodayIcon />, color: '#0d47a1' },
    { id: 'dashboard', label: t('navigation.dashboard'), icon: <DashboardIcon />, color: '#1976d2' },
    {
      id: 'quotes',
      label: t('navigation.quotes'),
      icon: <RequestQuote />,
      color: '#de6127',
      // TKT-573: Sales get All Quotes / Pending Quotes too (was Admin only).
      ...(hasMinRole(user, 'sales') ? {
        submenu: [
          { id: 'quotes', label: t('navigation.allQuotes'), icon: <RequestQuote />, color: '#de6127' },
          { id: 'pending-quotes', label: t('navigation.pendingQuotes'), icon: <Warning />, color: '#b54708' },
        ],
      } : {}),
    },
    { id: 'signatures', label: t('navigation.signatures'), icon: <SignaturesIcon />, color: '#1c3366' },
    { id: 'customers', label: t('navigation.customers'), icon: <People />, color: '#2e7d32' },
    { id: 'sales-tracker', label: t('navigation.salesTracker'), icon: <TrendingUp />, color: '#00897b' },
    { id: 'tickets', label: t('navigation.supportTickets'), icon: <SupportIcon />, color: '#9c27b0' },
    { id: 'tasks', label: t('pages.taskManagement'), icon: <TaskIcon />, color: '#ff5722' },
    { id: 'jobs', label: t('navigation.jobs'), icon: <Work />, color: '#d32f2f' },
    ...(user?.user_type === 'crew_manager' ? [
      { id: 'fleet-management', label: t('pages.fleetManagement'), icon: <LocalShipping />, color: '#1976d2' },
      { id: 'live-tracking', label: t('navigation.liveTracking'), icon: <Landscape />, color: '#00897b' }
    ] : []),
    { id: 'summer-logs', label: t('navigation.summerLogs'), icon: <GrassIcon />, color: '#2e7d32' },
    { id: 'winter-logs', label: t('navigation.winterLogs'), icon: <AcUnit />, color: '#0288d1' },
    { id: 'construction-logs', label: t('navigation.constructionLogs'), icon: <ConstructionIcon />, color: '#e65100' },
    { id: 'route-setup', label: t('navigation.routePlanning'), icon: <LocalShipping />, color: '#1565c0' },
    { id: 'routes-list', label: t('navigation.routesList'), icon: <LocalShipping />, color: '#1e88e5' },
    { id: 'crew-mode', label: t('navigation.crewMode'), icon: <PhoneIcon />, color: '#6a1b9a', external: true, href: '/crew' },
    { id: 'inspections', label: t('navigation.vehicleInspections'), icon: <InspectionIcon />, color: '#0288d1' },
    { id: 'documents', label: t('navigation.documents'), icon: <DocumentIcon />, color: '#795548' },
    { id: 'employee-forms', label: t('navigation.employeeForms'), icon: <AssignmentIcon />, color: '#1565c0' },
    { id: 'calendar', label: t('navigation.calendar'), icon: <CalendarToday />, color: '#7b1fa2' },
    { id: 'reports', label: t('navigation.reports'), icon: <Assessment />, color: '#f57c00' },
  ];

  const allAdminItems = [
    { id: 'users', label: t('navigation.userManagement'), icon: <AdminPanelSettings />, color: '#c62828' },
    { id: 'employees', label: t('navigation.employees'), icon: <BusinessIcon />, color: '#1565c0' },
    { id: 'hr-management', label: t('navigation.hrManagement'), icon: <People />, color: '#d32f2f',
      submenu: [
        { id: 'hr-dashboard', label: t('navigation.hrDashboard'), icon: <DashboardIcon />, color: '#d32f2f' },
        { id: 'timesheet-calendar', label: t('navigation.timesheetCalendar'), icon: <Schedule />, color: '#7b1fa2' },
        { id: 'leave-management', label: t('navigation.leaveManagement'), icon: <CalendarToday />, color: '#1976d2' },
        { id: 'absence-tracking', label: t('navigation.absenceTracking'), icon: <Warning />, color: '#f57c00' },
        { id: 'employee-discipline', label: t('navigation.employeeDiscipline'), icon: <TaskIcon />, color: '#c62828' },
        { id: 'performance-reviews', label: t('navigation.performanceReviews'), icon: <TrendingUp />, color: '#2e7d32' },
        { id: 'hr-documents', label: t('navigation.hrDocuments'), icon: <DocumentIcon />, color: '#5e35b1' },
        { id: 'employee-forms-admin', label: t('navigation.employeeFormsAdmin'), icon: <AssignmentIcon />, color: '#1565c0' }
      ]
    },
    { id: 'unsign-forms', label: t('navigation.unsignEmployeeForms'), icon: <AssignmentIcon />, color: '#c62828' },
    { id: 'admin-settings', label: t('navigation.adminSettings'), icon: <Settings />, color: '#5e35b1', 
      submenu: [
        { id: 'service-pricing', label: t('navigation.servicePricing'), icon: <AttachMoney />, color: '#4caf50' },
        { id: 'budget', label: t('navigation.budget'), icon: <BudgetIcon />, color: '#00796b' },
        { id: 'turf-admin', label: t('pages.turfProgramSettings'), icon: <GrassIcon />, color: '#2e7d32' },
        { id: 'quote-pdf-list', label: t('navigation.quotePdfList'), icon: <DocumentIcon />, color: '#d32f2f' },
        { id: 'quote-report', label: t('navigation.quoteReport'), icon: <RequestQuote />, color: '#795548' },
        { id: 'fleet-management', label: t('pages.fleetManagement'), icon: <LocalShipping />, color: '#1976d2' },
        { id: 'timesheets', label: t('navigation.timesheets'), icon: <Schedule />, color: '#7b1fa2' },
        { id: 'scheduler', label: t('navigation.jobScheduler'), icon: <Schedule />, color: '#f57c00' },
        { id: 'orphaned-job-sites', label: t('navigation.orphanedJobSites'), icon: <Warning />, color: '#f57c00' },
        { id: 'system-email-settings', label: t('navigation.systemEmailSettings'), icon: <EmailIcon />, color: '#00695c' }
      ]
    }
  ];

  // TKT-221: filter the navigation to the user's role tier. Each item's page
  // id maps to a minimum tier in utils/roles.js (PAGE_MIN_ROLE); items above
  // the user's level are hidden.
  const menuItems = allMenuItems.filter((item) => canAccessPage(user, item.id));
  // Submenus are filtered too, so admins see Admin Settings without the
  // superadmin-only Budget and pricing entries.
  const adminItems = allAdminItems
    .filter((item) => canAccessPage(user, item.id))
    .map((item) => (item.submenu
      ? { ...item, submenu: item.submenu.filter((sub) => canAccessPage(user, sub.id)) }
      : item));

  const drawer = (
    <Box>
      <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: '#000062', color: 'white' }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/logo.png`}
          alt="Paramount Landscaping Logo"
          sx={{
            mx: 'auto',
            width: '90%',
            height: 180,
            objectFit: 'contain'
          }}
        />
      </Box>

      <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {user?.first_name?.[0] || user?.username?.[0] || 'U'}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Chip 
              label={user?.user_type || 'User'} 
              size="small" 
              color={user?.user_type === 'admin' ? 'error' : 'primary'}
              sx={{ fontSize: '0.65rem', height: '16px' }}
            />
          </Box>
        </Box>
      </Box>

      <Divider />

      <List sx={{ pt: 1 }} data-demo-target="sidebar-main-list">
        {menuItems.map((item) => {
          const submenuSelected = item.submenu?.some((subItem) => selectedPage === subItem.id);
          const submenuOpen = Boolean(expandedMainMenus[item.id] || submenuSelected);

          return (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  data-demo-target={`sidebar-item-${item.id}`}
                  selected={!item.external && (
                    selectedPage === item.id
                    || submenuSelected
                    || (selectedPage === 'customer-detail' && item.id === 'customers')
                    || (selectedPage === 'ticket-detail' && item.id === 'tickets')
                    || (selectedPage === 'task-detail' && item.id === 'tasks')
                  )}
                  onClick={() => {
                    if (item.submenu) {
                      handleMainMenuToggle(item.id);
                      return;
                    }
                    if (item.external && item.href) {
                      // TKT-233: navigate top-level routes (e.g. /crew) in the
                      // SAME tab — Doug's single-tab requirement for iPad. The
                      // persistent Home button (TKT-232) brings the manager
                      // back to the dashboard.
                      routerNavigate(item.href);
                      setMobileOpen(false);
                      return;
                    }
                    clearDashboardDrilldownState();
                    navigateToPage(item.id);
                    setMobileOpen(false);
                  }}
                  aria-expanded={item.submenu ? submenuOpen : undefined}
                  aria-controls={item.submenu ? `sidebar-${item.id}-submenu` : undefined}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: `${item.color}15`,
                      borderLeft: `3px solid ${item.color}`,
                      '& .MuiListItemIcon-root': {
                        color: item.color,
                      },
                      '& .MuiListItemText-primary': {
                        color: item.color,
                        fontWeight: 'medium',
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                  {item.submenu && (submenuOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              {item.submenu && (
                <Collapse in={submenuOpen} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    id={`sidebar-${item.id}-submenu`}
                    data-demo-target={`sidebar-${item.id}-submenu`}
                  >
                    {item.submenu.map((subItem) => (
                      <ListItem key={subItem.id} disablePadding>
                        <ListItemButton
                          data-demo-target={`sidebar-item-${subItem.id}`}
                          selected={selectedPage === subItem.id}
                          onClick={() => {
                            clearDashboardDrilldownState();
                            navigateToPage(subItem.id);
                            setMobileOpen(false);
                          }}
                          aria-label={subItem.label}
                          sx={{
                            mx: 1,
                            ml: 5,
                            borderRadius: 1,
                            '&.Mui-selected': {
                              bgcolor: `${subItem.color}15`,
                              borderLeft: `3px solid ${subItem.color}`,
                              '& .MuiListItemIcon-root': { color: subItem.color },
                              '& .MuiListItemText-primary': {
                                color: subItem.color,
                                fontWeight: 'medium',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{ fontSize: '0.85rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {adminItems.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.7rem' }} data-demo-target="sidebar-admin-section">
            {t('navigation.administration')}
          </Typography>
          <List sx={{ pt: 0 }} data-demo-target="sidebar-admin-list">
            {adminItems.map((item) => (
              <React.Fragment key={item.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    data-demo-target={`sidebar-admin-${item.id}`}
                    selected={selectedPage === item.id || (item.submenu && item.submenu.some(subItem => selectedPage === subItem.id))}
                    onClick={() => {
                      if (item.submenu) {
                        handleAdminMenuToggle(item.id);
                      } else {
                        clearDashboardDrilldownState();
                        navigateToPage(item.id);
                        setMobileOpen(false);
                      }
                    }}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        bgcolor: `${item.color}15`,
                        borderLeft: `3px solid ${item.color}`,
                        '& .MuiListItemIcon-root': {
                          color: item.color,
                        },
                        '& .MuiListItemText-primary': {
                          color: item.color,
                          fontWeight: 'medium',
                        }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                    {item.submenu && (
                      expandedAdminMenus[item.id] ? <ExpandLess /> : <ExpandMore />
                    )}
                  </ListItemButton>
                </ListItem>
                {item.submenu && (
                  <Collapse in={expandedAdminMenus[item.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding data-demo-target={`sidebar-admin-${item.id}-submenu`}>
                      {item.submenu.map((subItem) => (
                        <ListItem key={subItem.id} disablePadding>
                          <ListItemButton
                            data-demo-target={`sidebar-admin-${subItem.id}`}
                            selected={selectedPage === subItem.id}
                            onClick={() => {
                              clearDashboardDrilldownState();
                              navigateToPage(subItem.id);
                              setMobileOpen(false);
                            }}
                            sx={{
                              mx: 1,
                              ml: 5, // Indent submenu items
                              borderRadius: 1,
                              '&.Mui-selected': {
                                bgcolor: `${subItem.color}15`,
                                borderLeft: `3px solid ${subItem.color}`,
                                '& .MuiListItemIcon-root': {
                                  color: subItem.color,
                                },
                                '& .MuiListItemText-primary': {
                                  color: subItem.color,
                                  fontWeight: 'medium',
                                }
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText 
                              primary={subItem.label}
                              primaryTypographyProps={{ fontSize: '0.85rem' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  const renderPageContent = () => {
    // TKT-221: block direct access (e.g. a typed URL) to modules above the
    // user's tier. Detail drill-downs map back to their entry-point module;
    // unmapped pages fall through as allowed.
    const PAGE_PARENT = {
      'customer-detail': 'customers',
      'ticket-detail': 'tickets',
      'task-detail': 'tasks',
      'inspection-new': 'inspections',
      'inspection-detail': 'inspections',
    };
    const guardPage = PAGE_PARENT[selectedPage] || selectedPage;
    if (!canAccessPage(user, guardPage)) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }} data-demo-target="page-access-denied">
          <Typography variant="h5" gutterBottom>
            {t('common.accessDenied', 'Access denied')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('common.accessDeniedHelp', "You don't have permission to view this section.")}
          </Typography>
        </Box>
      );
    }

    switch (selectedPage) {
      case 'welcome':
        return (
          <Box>
            <Typography 
              variant="h4" 
              sx={{ mb: 3, fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.5rem', md: '2.125rem' } }}
            >
              {t('common.welcome')} to Paramount Landscaping Management System
            </Typography>
            <Paper sx={{ p: { xs: 3, md: 6 }, borderRadius: 3, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#de6127' }}>
                {t('common.comingSoon')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {t('features.developmentMessage')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('common.whatsComing')}
              </Typography>
              <Box component="ul" sx={{ textAlign: 'left', pl: 3, mb: 3 }}>
                <li>{t('navigation.customers')}</li>
                <li>{t('pages.jobsAndRouteManagement')}</li>
                <li>{t('pages.quoteManagement')}</li>
                <li>{t('navigation.supportTickets')}</li>
                <li>{t('pages.reportsAndAnalytics')}</li>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('features.developmentMessage')}
              </Typography>
            </Paper>
          </Box>
        );
      case 'operations':
        return (
          <OperationsDashboard
            onNavigatePage={(page) => navigateToPage(page)}
          />
        );
      case 'dashboard':
        // Check user role to determine dashboard content
        const isAdmin = hasMinRole(user, 'admin');

        if (!isAdmin) {
          // Employee / Sales tiers: time tracking only. TKT-221 hides the
          // "Today's Jobs" section for these tiers.
          return (
            <Box sx={{ width: '100%', maxWidth: 'none', mx: 0, px: 0 }} data-demo-target="page-dashboard-root">
              <Typography 
                variant="h4" 
                sx={{ mb: 3, fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.5rem', md: '2.125rem' } }}
              >
                Welcome, {user?.first_name || user?.username}
              </Typography>
              <Box data-demo-target="page-dashboard-time-tracking">
                <TimeTrackingWidget />
              </Box>
              <Box sx={{ mt: 4 }}>
                <InsightsCenter />
              </Box>
            </Box>
          );
        }

        // Admin/Superadmin users see full dashboard + time tracking
        return (
          <Box sx={{ width: '100%', maxWidth: 'none', mx: 0, px: 0 }} data-demo-target="page-dashboard-root">
            <Typography 
              variant="h4" 
              sx={{ mb: 3, fontWeight: 'bold', color: '#000062', fontSize: { xs: '1.5rem', md: '2.125rem' } }}
            >
              {t('dashboard.title')}
            </Typography>

            {/* Time Tracking Section - First Priority */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h5" 
                sx={{ mb: 3, fontWeight: 'bold', color: '#2e7d32', display: 'flex', alignItems: 'center' }}
              >
                <Schedule sx={{ mr: 1, fontSize: '1.8rem' }} />
                Time Tracking
              </Typography>
              <TimeTrackingWidget />
            </Box>

            {/* Today's Jobs - Quick Clock In/Out */}
            <Box sx={{ mb: 4 }}>
              <TodaysJobsWidget onJobClick={handleNavigateToJob} />
            </Box>

            {/* Live Metrics Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, width: '100%', flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }, minWidth: 0 }}>
                <StatCard
                  title={t('metrics.activeJobs')}
                  value={liveMetrics.activeJobs}
                  subtitle={t('metrics.scheduledToday', 'Scheduled today')}
                  icon={<Work />}
                  color="#2e7d32"
                  trend="up"
                  trendValue="+12%"
                  ariaLabel={t('metrics.openActiveJobs')}
                  onClick={() => handleMetricCardNavigate({
                    page: 'jobs',
                    jobsFilter: { status: 'active' }
                  })}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }, minWidth: 0 }}>
                <StatCard
                  title={t('metrics.pendingQuotes')}
                  value={liveMetrics.pendingQuotes}
                  subtitle={t('metrics.awaitingApproval')}
                  icon={<RequestQuote />}
                  color="#de6127"
                  trend="up"
                  trendValue="+8%"
                  ariaLabel={t('metrics.openPendingQuotes')}
                  onClick={() => navigateToPage('pending-quotes')}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }, minWidth: 0 }}>
                <StatCard
                  title={t('metrics.revenue')}
                  value={formatCurrencyAbbreviated(liveMetrics.revenue)}
                  subtitle={t('metrics.thisMonth')}
                  icon={<AttachMoney />}
                  color="#1976d2"
                  trend="up"
                  trendValue="+18%"
                  ariaLabel={t('metrics.openRevenueReport')}
                  onClick={() => handleMetricCardNavigate({
                    page: 'reports',
                    reportId: 'truck-profit-margin'
                  })}
                  demo
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }, minWidth: 0 }}>
                <StatCard
                  title={t('metrics.efficiency')}
                  value={`${liveMetrics.efficiency}%`}
                  subtitle={t('metrics.teamPerformance')}
                  icon={<Analytics />}
                  color="#9c27b0"
                  trend="up"
                  trendValue="+5%"
                  ariaLabel={t('metrics.openEfficiencyReport')}
                  onClick={() => handleMetricCardNavigate({
                    page: 'reports',
                    reportId: 'time-tracking'
                  })}
                  demo
                />
              </Box>
            </Box>

            {/* Charts Section */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, width: '100%' }}>
              {/* Revenue Trend Chart - 70% width */}
              <Box sx={{ width: '70%', minWidth: 0 }}>
                <RevenueChart />
              </Box>

              {/* Service Distribution - 30% width */}
              <Box sx={{ width: '30%', minWidth: 0 }}>
                <ServiceChart />
              </Box>
            </Box>

            {/* Truck Profitability and Performance */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, width: '100%' }}>
              {/* Truck Profitability - 65% width */}
              <Box sx={{ width: '65%', minWidth: 0 }}>
                <TruckChart />
              </Box>

              {/* Weekly Performance - 35% width */}
              <Box sx={{ width: '35%', minWidth: 0 }}>
                <WeeklyChart />
              </Box>
            </Box>

            {/* Additional KPIs */}
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Box sx={{ width: '25%', minWidth: 0 }}>
                <StatCard
                  title={t('metrics.customerSatisfaction')}
                  value={`${liveMetrics.customerSatisfaction}/5.0`}
                  subtitle={t('metrics.averageRating')}
                  icon={<CheckCircle />}
                  color="#4caf50"
                  trend="up"
                  trendValue="+0.2"
                  demo
                />
              </Box>
              <Box sx={{ width: '25%', minWidth: 0 }}>
                <StatCard
                  title={t('metrics.newCustomers')}
                  value={liveMetrics.newCustomers}
                  subtitle={t('metrics.thisWeek')}
                  icon={<People />}
                  color="#d32f2f"
                  trend="up"
                  trendValue="+33%"
                  demo
                />
              </Box>
              <Box sx={{ width: '25%', minWidth: 0 }}>
                <StatCard
                  title={t('metrics.equipmentStatus')}
                  value="94%"
                  subtitle={t('metrics.operational')}
                  icon={<LocalShipping />}
                  color="#ff9800"
                  trend="up"
                  trendValue="+2%"
                  demo
                />
              </Box>
              <Box sx={{ width: '25%', minWidth: 0 }}>
                <StatCard
                  title={t('metrics.weatherAlert')}
                  value={t('metrics.clear')}
                  subtitle={t('metrics.perfectForOutdoorWork')}
                  icon={<WbSunny />}
                  color="#03a9f4"
                  demo
                />
              </Box>
            </Box>

            {/* Vehicle Inspections Widget */}
            <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 2 }}>
              <Box sx={{ width: '100%' }}>
                <InspectionsDashboardWidget 
                  onNavigateToInspections={() => navigateToPage('inspections')}
                  onNavigateToNewInspection={() => navigateToPage('inspection-new')}
                />
              </Box>
            </Box>

            {/* Advanced KPI Charts */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Box sx={{ width: '50%', minWidth: 0 }}>
                  <LaborEfficiencyChart />
                </Box>
                <Box sx={{ width: '50%', minWidth: 0 }}>
                  <JobProfitChart />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Box sx={{ width: '50%', minWidth: 0 }}>
                  <QuoteFunnelChart />
                </Box>
                <Box sx={{ width: '50%', minWidth: 0 }}>
                  <TicketSLAChart />
                </Box>
              </Box>
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <FleetUtilizationChart />
              </Box>
            </Box>

            {/* Live role-scoped insights */}
            <Box sx={{ mt: 4 }}>
              <InsightsCenter />
            </Box>

          </Box>
        );
      case 'quotes':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#de6127', textAlign: 'left' }}>
              Quote Management
            </Typography>
            <QuoteList key={`quotes-${quotesInitialStatus || 'all'}-${quoteActionKey || 'none'}`} initialStatus={quotesInitialStatus} />
          </Box>
        );
      case 'pending-quotes':
        return <PendingQuotesPage />;
      case 'signatures':
        return <SignaturesPage />;
      case 'customers':
        return <CRMDashboard onNavigateToCustomer={handleNavigateToCustomer} />;
      case 'sales-tracker':
        return <SalesTracker onNavigateToCustomer={handleNavigateToCustomer} />;
      case 'customer-detail':
        return customerDetailId ? <CustomerDetailView customerId={customerDetailId} onBack={handleBackToCustomers} /> : null;
      case 'tickets':
        return <TicketList onNavigateToTicket={handleNavigateToTicket} />;
      case 'ticket-detail':
        return ticketDetailId ? <TicketDetailPage ticketId={ticketDetailId} onBack={handleBackToTickets} /> : null;
      case 'tasks':
        return <TaskList selectedTaskId={taskDetailId} onNavigateToTask={handleNavigateToTask} onBack={handleBackToTasks} />;
      case 'task-detail':
        // This case is now handled by the 'tasks' case with selectedTaskId
        return <TaskList onNavigateToTask={handleNavigateToTask} />;
      case 'summer-logs':
        // Pre-filtered view of summer jobs. The `key` forces React to remount
        // JobManagement when switching between Jobs / Summer / Winter /
        // Construction so its `initialFilter`-seeded state actually takes.
        return (
          <JobManagement
            key="logs-summer"
            onNavigateToJob={handleNavigateToJob}
            initialFilter={{ jobType: 'summer' }}
          />
        );
      case 'winter-logs':
        return (
          <JobManagement
            key="logs-winter"
            onNavigateToJob={handleNavigateToJob}
            initialFilter={{ jobType: 'winter' }}
          />
        );
      case 'construction-logs':
        return (
          <JobManagement
            key="logs-construction"
            onNavigateToJob={handleNavigateToJob}
            initialFilter={{ jobType: 'construction' }}
          />
        );
      case 'jobs':
        return (
          <JobManagement
            key={`jobs-${JSON.stringify(jobsInitialFilter || {})}`}
            onNavigateToJob={handleNavigateToJob}
            initialFilter={jobsInitialFilter || undefined}
          />
        );
      case 'live-tracking':
        // TKT-181: deep-link straight to the Live Tracking tab inside
        // Job Management so "Full Live Tracking" buttons land on the
        // fleet map instead of the Jobs list.
        return (
          <JobManagement
            key="jobs-live-tracking"
            onNavigateToJob={handleNavigateToJob}
            initialView="tracking"
          />
        );
      case 'job-detail':
        return jobDetailId ? <JobDetailView jobId={jobDetailId} onBack={handleBackToJobs} /> : null;
      case 'documents':
        return <DocumentsPage />;
      case 'route-setup':
        return <RouteSetup />;
      case 'routes-list':
        return <RoutesList />;
      case 'inspections':
        return <VehicleInspections onNavigateToNew={() => navigateToPage('inspection-new')} onNavigateToInspection={handleNavigateToInspection} />;
      case 'inspection-new':
        return <InspectionWizard onBack={() => navigateToPage('inspections')} />;
      case 'inspection-detail':
        return inspectionDetailId ? <InspectionDetailView inspectionId={inspectionDetailId} onBack={handleBackToInspections} /> : null;
      case 'users':
          return <UserManagement />;
        case 'employees':
          return <AdminEmployeeManagement />;
        case 'turf-admin':
          return <TurfAdminPanel />;
        case 'service-pricing':
          return <ServicePricingAdmin />;
        case 'budget':
          return <BudgetAdmin />;
        case 'quote-pdf-list':
          return <QuotePdfList />;
        case 'quote-report':
          return <ReportsMain key="admin-quote-report" initialReportId="quotes" />;
        case 'fleet-management':
          return <FleetManagement readOnly={user?.user_type === 'crew_manager'} />;
        case 'timesheets':
          return <TimesheetManagement />;
        case 'scheduler':
          return <SchedulerManagement />;
        case 'orphaned-job-sites':
          return <OrphanedJobSites />;
        case 'system-email-settings':
          return <SystemEmailSettings />;
        case 'hr-dashboard':
          return <HRDashboard onNavigate={(page) => navigateToPage(page)} />;
        case 'employee-forms':
          if (selectedFormId) {
            // Render specific form
            const commonProps = {
              language: formLanguage,
              onSubmit: async (formData, signatureData) => {
                try {
                  const response = await fetch('/api/employee-forms/submit', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                      form_type_id: selectedFormId,
                      language: formLanguage,
                      form_data: formData,
                      signature_data: signatureData
                    })
                  });
                  const data = await response.json();
                  if (!response.ok) {
                    throw new Error(data.error);
                  }
                  alert(data.message || 'Form submitted successfully');
                  setSelectedFormId(null);
                  navigateToPage('employee-forms');
                } catch (err) {
                  alert(err.message || 'Error submitting form');
                }
              },
              loading: false,
              disabled: false
            };

            return (
              <Box>
                <Button 
                  onClick={() => setSelectedFormId(null)} 
                  startIcon={<ArrowBack />}
                  sx={{ mb: 2 }}
                >
                  {formLanguage === 'es' ? 'Volver a Formularios' : 'Back to Forms'}
                </Button>
                {selectedFormId === 1 && <CompanyPolicyForm {...commonProps} />}
                {selectedFormId === 2 && <PayrollAgreementForm {...commonProps} />}
                {selectedFormId === 3 && <UniformForm {...commonProps} />}
                {selectedFormId === 4 && <InterestConflictPolicyForm {...commonProps} />}
                {selectedFormId === 5 && <EmployeeEmergencyInfoForm {...commonProps} />}
                {selectedFormId === 6 && <HumanResourcesPolicyForm {...commonProps} />}
                {selectedFormId === 7 && <DisciplinePolicyForm {...commonProps} />}
                {selectedFormId === 8 && <CellPhoneForm {...commonProps} />}
                {selectedFormId === 9 && <DirectDepositForm {...commonProps} />}
                {selectedFormId === 10 && <ViolencePreventionPolicyForm {...commonProps} />}
                {selectedFormId === 11 && <DrugAlcoholPolicyForm {...commonProps} />}
                {selectedFormId === 12 && <HealthSafetyPolicyForm {...commonProps} />}
                {selectedFormId === 13 && <EquipmentPolicyForm {...commonProps} />}
                {selectedFormId === 14 && <EmploymentStandardsActForm {...commonProps} />}
                {selectedFormId === 15 && <GasAirMilesCardForm {...commonProps} />}
                {selectedFormId === 16 && <ElectronicDevicePolicyForm {...commonProps} />}
              </Box>
            );
          }
          return <FormIndex onFormSelect={(formId, lang) => {
            setSelectedFormId(formId);
            setFormLanguage(lang);
          }} />;
        case 'timesheet-calendar':
          return <TimesheetCalendar />;
        case 'leave-management':
          return <LeaveManagement />;
        case 'absence-tracking':
          return <AbsenceTracking />;
        case 'employee-discipline':
          return <EmployeeDiscipline />;
        case 'performance-reviews':
          return <PerformanceReviews />;
        case 'hr-documents':
          return <HRDocuments />;
        case 'employee-forms-admin':
          return <EmployeeFormsAdmin />;
        case 'unsign-forms':
          return <UnsignEmployeeForms />;
        case 'calendar':
          return (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2', textAlign: 'left' }}>
                  {t('pages.calendarManagement')}
                </Typography>
              </Box>
              <CalendarView onNavigateToJob={handleNavigateToJob} />
            </Box>
          );
        case 'reports':
          return <ReportsMain key={`reports-${reportsInitialReportId || 'menu'}`} initialReportId={reportsInitialReportId} />;
        case 'profile':
          return <UserProfile />;
        default:
          return (
            <Box>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'left' }}>
                {selectedPage.charAt(0).toUpperCase() + selectedPage.slice(1)}
              </Typography>
              <Typography variant="body1">
                This section is under development and will be implemented in future updates.
              </Typography>
            </Box>
          );
      }
    };



  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'black',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }} data-demo-target="appbar-widgets">
            <WeatherWidget location="Toronto, Ontario, Canada" />
            <TimeDisplay />
          </Box>

          {/* TKT-232 (Doug): persistent Home button so users can always get
              back to the main dashboard without signing out — mirrors the
              legacy app, where a "Home" link was reachable from every page. */}
          <MuiTooltip title={t('common.home')}>
            <IconButton
              color="inherit"
              onClick={() => navigateToPage('dashboard')}
              aria-label={t('common.goHome')}
              data-demo-target="appbar-home"
            >
              <HomeIcon />
            </IconButton>
          </MuiTooltip>

          <Box data-demo-target="appbar-language"><LanguageSwitcher variant="menu" /></Box>
          <Box data-demo-target="appbar-notifications"><NotificationDropdown onNavigateToTask={handleNavigateToTask} onViewTaskManagement={() => navigateToPage('tasks')} /></Box>
          
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
            data-demo-target="appbar-account"
            sx={{ 
              p: { xs: 1, sm: 1.5 }
            }}
          >
            <AccountCircle />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t('common.logout')}
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 1, md: 1.5 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: '#fafafa',
          minHeight: 'calc(100vh - 64px)',
          maxWidth: 'none',
          pb: showBackToCrew ? { xs: 12, sm: 10 } : undefined
        }}
      >
        <ErrorBoundary
          resetKey={selectedPage}
          onGoHome={() => navigateToPage('dashboard')}
        >
          {renderPageContent()}
        </ErrorBoundary>
      </Box>

      <Dialog
        open={pendingApprovalOpen}
        onClose={() => setPendingApprovalOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="pending-quote-approvals-title"
      >
        <DialogTitle id="pending-quote-approvals-title">
          {t('quotes.pendingApprovalPopupTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('quotes.pendingApprovalPopupIntro', { count: pendingApprovalQuotes.length })}
          </Typography>
          <List disablePadding>
            {pendingApprovalQuotes.map((pendingQuote) => {
              const customerName = pendingQuote.customer_name
                || pendingQuote.company_name
                || `${pendingQuote.first_name || ''} ${pendingQuote.last_name || ''}`.trim();
              const label = `${pendingQuote.quote_number || pendingQuote.id} — ${customerName}`;
              return (
                <ListItem key={pendingQuote.id} disablePadding divider>
                  <ListItemButton
                    onClick={() => handlePendingQuoteOpen(pendingQuote.id)}
                    aria-label={t('quotes.reviewPendingQuoteAria', { quote: label })}
                  >
                    <ListItemIcon>
                      <RequestQuote color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={pendingQuote.quote_number || `#${pendingQuote.id}`}
                      secondary={customerName}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button onClick={() => setPendingApprovalOpen(false)} aria-label={t('common.close')}>
            {t('common.close')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPendingApprovalOpen(false);
              navigateToPage('pending-quotes');
            }}
            aria-label={t('quotes.openAllPendingApprovals')}
          >
            {t('quotes.openAllPendingApprovals')}
          </Button>
        </DialogActions>
      </Dialog>

      {showBackToCrew && (
        <Fab
          variant="extended"
          color="primary"
          onClick={() => routerNavigate('/crew')}
          aria-label={t('crew.backToCrewModeAria')}
          sx={{
            position: 'fixed',
            left: { xs: 16, sm: `${drawerWidth + 24}px` },
            bottom: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', sm: 24 },
            zIndex: (theme) => theme.zIndex.fab,
            maxWidth: 'calc(100vw - 32px)',
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          <PhoneIcon sx={{ mr: 1 }} />
          {t('crew.backToCrewMode')}
        </Fab>
      )}
    </Box>
  );
};

export default Dashboard; 